import React, { useMemo } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import { StackedColumnCategoryChart, StackedColumnsCategoryChartProps } from '../../../../components/chart/stackedColumnsCategoryChart';
import { ISmartBatteryBatteryAgeIntervalStats } from '../../../../services/shared/battery/batteryCentricService';
import Card from '@components/card/card';
import '../style.css';
import { createChartTooltipAdapter, getTitle } from './lib';

interface IProps {
  data: ISmartBatteryBatteryAgeIntervalStats,
  isLoading?: boolean
}

interface BatteryAgeChartData {
  bucket: string,
  count: number
}

const series = [
  {
    visible: true,
    dataKey: 'count' as keyof BatteryAgeChartData,
    description: 'Batteries Health Count',
    colour: '#5b9ad2'
  },
];

export function BatteryAgeChartContainer(props: IProps) {
  const ns = 'batteryEssentialsChart';
  const { t } = useTranslation(ns);

  const chartData: BatteryAgeChartData[] = useMemo(() => {
    if (!props.data) return [];

    const data = [
      {
        bucket: '0-6',
        count: props.data.months0To6
      },
      {
        bucket: '6-12',
        count: props.data.months6To12
      },
      {
        bucket: '12-18',
        count: props.data.months12To18
      },
      {
        bucket: '18-24',
        count: props.data.months18To24
      },
      {
        bucket: '24-36',
        count: props.data.months24To36
      },
      {
        bucket: '>36',
        count: props.data.over36Months
      },
      {
        bucket: t('UNKNOWN'),
        count: props.data.unknown
      }

    ];
    return data;
  }, [props.data, t]);

  const chartProps: StackedColumnsCategoryChartProps<BatteryAgeChartData> = {
    data: !props.isLoading ? chartData : null,
    series,
    title: getTitle(t, 'BATTERY_AGE_CHART_TITLE'),
    cypressId: 'battery-age-chart',
    className: 'battery-age-chart',
    hasLegend: false,
    bottomBorder: false,
    columnWidth: 50,
    yAxisType: 'value',
    useAxisTooltip: true,
    amchartsYAxisNumberFormat: '#.',
    categoryKey: 'bucket',
    drawXAxis: (chart, xAxis) => {
      xAxis.title.text = t('BATTERY_AGE_CHART_LABEL');
      xAxis.renderer.labels.template.fontSize = 12;
      xAxis.renderer.labels.template.horizontalCenter = 'middle';
      xAxis.renderer.labels.template.verticalCenter = 'middle';
      xAxis.renderer.minGridDistance = 20;
      return xAxis;
    },
    drawYAxis: (chart, yAxis) => {
      yAxis.title.text = t('NO_OF_BATTERIES');
      yAxis.title.fontSize = 12;
      yAxis.renderer.labels.template.fontSize = 12;
      yAxis.min = 0;
      yAxis.strictMinMax = false;
      yAxis.renderer.minGridDistance = 30;
      yAxis.maxPrecision = 0;
      return yAxis;
    },
    getAxisTooltipAdapter: () => createChartTooltipAdapter(t)
  };

  return (
    <Card noPadding={false} className="battery-age-container">
      <StackedColumnCategoryChart<any>
        {...chartProps}
      />
    </Card>
  );
}
